/**
 * Minimal hash-based router for the web app.
 *
 * Three routes today:
 *   - `#/` (or no hash) → main inbox (MainPage)
 *   - `#/c/:conversationKey` → MainPage with the SW's deep-link key
 *     (the SW `notificationclick` handler sets this URL when the
 *     user taps the OS notification body; MainPage reads the key and
 *     scrolls to + highlights the thread)
 *   - `#/settings` → SettingsPage (theme picker + disconnect)
 *
 * Hash-based instead of `History API` so the SW can cheap-and-cheerfully
 * push a focused tab to a new URL via `client.navigate("/#/c/foo")`
 * without needing a history stack manipulation that would round-trip
 * through the page React tree first. Hash routing also avoids the
 * server having to know about `/c/:key` when the static host serves
 * `index.html` for unknown paths.
 *
 * Conversation key encoding: the key is `packageName|id|tag` with `|`
 * separators (per `apps/extension/src/storage/conversations.ts` /
 * `@reflecto/client-core`). `encodeURIComponent` round-trips them
 * losslessly through the hash.
 *
 * No router dependency — `react-router` would pull ~16 KB minified
 * for three routes that need ~30 lines of code. If a third surface
 * (Electron?) ever needs the same scheme, lift this into a shared
 * client-core helper at that point.
 */

import { useEffect, useState } from "react";

export type Route =
  | { name: "main" }
  | { name: "conversation"; conversationKey: string }
  | { name: "settings" };

export function parseHash(hash: string): Route {
  // `location.hash` includes the leading `#`; strip it and treat
  // empty / `/` as the default route.
  const path = hash.replace(/^#/, "");
  if (path === "" || path === "/") return { name: "main" };
  if (path === "/settings") return { name: "settings" };
  // `/c/:key` — the key portion may itself contain encoded `|`
  // separators (`%7C`); `decodeURIComponent` reverses the
  // navigateToConversation encode.
  const conversationMatch = path.match(/^\/c\/(.+)$/);
  if (conversationMatch?.[1]) {
    try {
      return { name: "conversation", conversationKey: decodeURIComponent(conversationMatch[1]) };
    } catch {
      // Malformed escape — fall through to main.
    }
  }
  return { name: "main" };
}

/**
 * Build the hash URL for a conversation deep-link. Used by the SW
 * (`apps/web/src/sw/index.ts` notificationclick handler) and any
 * future in-app deep link.
 */
export function conversationHash(conversationKey: string): string {
  return `#/c/${encodeURIComponent(conversationKey)}`;
}

/**
 * React hook returning the current parsed route, re-rendering on
 * `hashchange`. Drives the App-level switch between PairingPage /
 * MainPage / SettingsPage.
 */
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() =>
    typeof window === "undefined" ? { name: "main" } : parseHash(window.location.hash)
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onChange = () => setRoute(parseHash(window.location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
}

/**
 * Web Share Target landing path.
 *
 * The PWA manifest's `share_target.action` is the RELATIVE URL
 * `"share-target"`, which the UA resolves against the manifest's own
 * URL — so it lands at `${BASE_URL}share-target` (`/share-target` in
 * production, `/pr-123/share-target` on a per-PR preview). This is a
 * REAL path navigation (not a hash route): the OS share sheet opens the
 * PWA at that URL with `?title=&text=&url=` query params, the static
 * host serves `index.html` for it (same SPA-fallback the conversation
 * deep-links rely on), and `App` detects it here before the hash router.
 *
 * `import.meta.env.BASE_URL` is Vite's build-time-resolved `base`
 * (always ends with `/`), so this comparison is VITE_BASE_PATH-aware
 * without hardcoding `/`.
 */
export function isShareTargetPath(pathname: string = window.location.pathname): boolean {
  const base = import.meta.env.BASE_URL; // e.g. "/" or "/pr-123/"
  return pathname === `${base}share-target` || pathname === `${base}share-target/`;
}

/**
 * Pull the shared payload off the Share Target navigation URL. The
 * manifest declares `text`, `title`, and `url` params; we prefer
 * `text`, fall back to `url`, then `title`, joining what's present so a
 * "share a link" intent (URL only) still produces sendable content.
 */
export function readSharedText(search: string = window.location.search): string {
  const params = new URLSearchParams(search);
  const text = params.get("text") ?? "";
  const url = params.get("url") ?? "";
  const title = params.get("title") ?? "";
  // Most shares populate `text`; link shares populate `url`. Combine
  // non-empty parts, deduped, so neither flavour loses content and a
  // link share that fills both `text` and `url` with the same URL
  // doesn't yield "url\nurl".
  const parts = [...new Set([text, url])].filter((p) => p.length > 0);
  // `Array.join` ignores the separator for a single element, so a
  // plain "\n" join handles both the one-part and two-part cases.
  const joined = parts.join("\n");
  return joined || title;
}

/** Imperative navigation — sets `location.hash` and triggers `hashchange`. */
export function navigate(route: Route): void {
  if (typeof window === "undefined") return;
  switch (route.name) {
    case "main":
      window.location.hash = "/";
      break;
    case "settings":
      window.location.hash = "/settings";
      break;
    case "conversation":
      window.location.hash = `/c/${encodeURIComponent(route.conversationKey)}`;
      break;
  }
}
